/**
 * Marketing site CTAs open `/signup?plan=10|25|50`.
 * Those values are license counts, not Stripe package ids.
 *
 *   plan  licenses   monthly price   package (matched from the API)
 *   10    Team 10    49,99 €         payment package with maxLicenses 10
 *   25    Team 25    119,99 €        payment package with maxLicenses 25
 *   50    Team 50    219,99 €        payment package with maxLicenses 50
 *
 * Checkout still posts the API package id (`POST /payments/checkout-session`).
 */

export const MARKETING_PLANS = [10, 25, 50] as const

export type MarketingPlan = (typeof MARKETING_PLANS)[number]

/** Public list prices in euros, matching the marketing site. */
export const MARKETING_PLAN_PRICES_EUR: Record<MarketingPlan, number> = {
  10: 49.99,
  25: 119.99,
  50: 219.99,
}

const PENDING_PLAN_KEY = 'startsmart.pendingMarketingPlan'

export type MarketingPackageLike = {
  id: string
  name?: string
  amount: number | string
  maxLicenses?: number | null
  billingInterval?: string | null
  isActive?: boolean
}

export function parseMarketingPlan(value: string | null | undefined): MarketingPlan | null {
  if (value == null) return null
  const trimmed = value.trim()
  if (trimmed === '10' || trimmed === '25' || trimmed === '50') {
    return Number(trimmed) as MarketingPlan
  }
  return null
}

export function marketingPlanPriceLabel(plan: MarketingPlan): string {
  return `${MARKETING_PLAN_PRICES_EUR[plan].toFixed(2).replace('.', ',')} €`
}

export function withPlanQuery(path: string, plan: MarketingPlan | null | undefined): string {
  if (!plan) return path
  const hashIndex = path.indexOf('#')
  const hash = hashIndex >= 0 ? path.slice(hashIndex) : ''
  const withoutHash = hashIndex >= 0 ? path.slice(0, hashIndex) : path
  const queryIndex = withoutHash.indexOf('?')
  const pathname = queryIndex >= 0 ? withoutHash.slice(0, queryIndex) : withoutHash
  const params = new URLSearchParams(queryIndex >= 0 ? withoutHash.slice(queryIndex + 1) : '')
  params.set('plan', String(plan))
  const query = params.toString()
  return `${pathname}${query ? `?${query}` : ''}${hash}`
}

export function postAuthPath(plan: MarketingPlan | null | undefined): string {
  return plan ? `/subscription?plan=${plan}` : '/'
}

export function destinationAfterAuth(
  role: string | undefined,
  plan: MarketingPlan | null | undefined
): string {
  if (plan && role !== 'EMPLOYEE') return postAuthPath(plan)
  return '/'
}

export function rememberPendingPlan(plan: MarketingPlan) {
  try {
    sessionStorage.setItem(PENDING_PLAN_KEY, String(plan))
  } catch {
    // Private mode or a blocked storage API should not break signup.
  }
}

export function readPendingPlan(): MarketingPlan | null {
  try {
    return parseMarketingPlan(sessionStorage.getItem(PENDING_PLAN_KEY))
  } catch {
    return null
  }
}

export function clearPendingPlan() {
  try {
    sessionStorage.removeItem(PENDING_PLAN_KEY)
  } catch {
    // Ignore storage failures.
  }
}

export function amountsMatch(amount: number | string, expectedEuros: number): boolean {
  const value = Number(amount)
  if (!Number.isFinite(value)) return false
  if (Math.abs(value - expectedEuros) < 0.02) return true
  // Some payloads use minor units (4999 = 49.99 EUR).
  return Math.abs(value - Math.round(expectedEuros * 100)) < 1
}

function nameSuggestsPlan(name: string | undefined, plan: MarketingPlan): boolean {
  if (!name) return false
  const normalized = name.toLowerCase()
  return (
    new RegExp(`\\bteam\\s*${plan}\\b`).test(normalized) ||
    new RegExp(`\\b${plan}\\s*(lizenzen|licenses|lizenz|license)\\b`).test(normalized)
  )
}

function packageMatchesPlan(pkg: MarketingPackageLike, plan: MarketingPlan): boolean {
  if (Number(pkg.maxLicenses) === plan) return true
  if (pkg.maxLicenses == null && nameSuggestsPlan(pkg.name, plan)) return true
  return false
}

/**
 * Resolve a marketing license count to the Stripe package the app already sells.
 * Prefer an active package with that `maxLicenses`, then the published monthly price.
 */
export function findPackageForMarketingPlan<T extends MarketingPackageLike>(
  packages: T[],
  plan: MarketingPlan
): T | null {
  const active = packages.filter(pkg => pkg.isActive !== false)
  const byPlan = active.filter(pkg => packageMatchesPlan(pkg, plan))
  const expected = MARKETING_PLAN_PRICES_EUR[plan]
  const pool = byPlan.length > 0 ? byPlan : active.filter(pkg => amountsMatch(pkg.amount, expected))

  if (pool.length === 0) return null

  return [...pool].sort((a, b) => {
    const priceRank = (pkg: T) => (amountsMatch(pkg.amount, expected) ? 0 : 1)
    const intervalRank = (pkg: T) => ((pkg.billingInterval || 'month') === 'month' ? 0 : 1)
    const paidRank = (pkg: T) => (Number(pkg.amount) > 0 ? 0 : 1)
    return (
      priceRank(a) - priceRank(b) ||
      intervalRank(a) - intervalRank(b) ||
      paidRank(a) - paidRank(b) ||
      Number(a.amount) - Number(b.amount)
    )
  })[0]
}

const BLOCKING_SUBSCRIPTION_STATUSES = new Set([
  'active',
  'trialing',
  'past_due',
  'unpaid',
  'paused',
])

export function hasBlockingSubscription(
  status?: string | null,
  stripeSubscriptionId?: string | null
): boolean {
  if (!stripeSubscriptionId) return false
  return BLOCKING_SUBSCRIPTION_STATUSES.has((status || '').toLowerCase())
}

export function decideAutoCheckout(input: {
  plan: MarketingPlan | null
  packageId: string | null
  hasStripeSubscription: boolean
  activePackageId?: string | null
  activeTier?: number | null
  targetTier?: number | null
}): { action: 'checkout'; packageId: string } | { action: 'preselect' } {
  if (!input.plan || !input.packageId) return { action: 'preselect' }
  if (!input.hasStripeSubscription) return { action: 'checkout', packageId: input.packageId }
  if (input.activePackageId && input.activePackageId === input.packageId) {
    return { action: 'preselect' }
  }
  if (input.activeTier == null || input.targetTier == null) return { action: 'preselect' }
  if (input.targetTier <= input.activeTier) return { action: 'preselect' }
  return { action: 'checkout', packageId: input.packageId }
}
